import { useState, useEffect } from 'react'
import { HeaderHLPure, SimpleLoading, TabPure } from '../../pures'
import { Container, Grid } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { TokenVolumeGraph, StatCard, SimpleCard, ExpenseSpeedometer, SentimentDailyGraph, AttentionStatusGraph } from '../../pures/dashboard'
import { DropDownList } from "@progress/kendo-react-dropdowns"
import { Button } from '@progress/kendo-react-buttons'
import { arrowRotateCwIcon } from '@progress/kendo-svg-icons'

const UsageView = (props) => {
    const ENV = process.env
    const {
        isLoading,
        usagePart1,
        dailyTokenUsage,
        sentimentData,
        date,
        handleChangeDate,
        monthYearOptions,
        sentimentEnabled,
        handleSentimentClick,
        onRefresh
    } = props

    const { t, i18n } = useTranslation('clients-dashboard', { keyPrefix: 'reportsScreen' })

    const [tabSelected, setTabSelected] = useState(ENV.REACT_APP_SENTIMENT_REPORT)

    // Si el bot no tiene análisis de sentimiento, forzar la pestaña general
    useEffect(() => {
        if (!sentimentEnabled && tabSelected === ENV.REACT_APP_SENTIMENT_REPORT) {
            setTabSelected(ENV.REACT_APP_GENERAL_REPORT)
        } else {
            setTabSelected(ENV.REACT_APP_SENTIMENT_REPORT)
        }
    }, [sentimentEnabled]) // eslint-disable-line react-hooks/exhaustive-deps

    const tabs = [
        ...(sentimentEnabled ? [{
            route: ENV.REACT_APP_SENTIMENT_REPORT,
            title: t('tabs.sentimentAnalysis'),
            key: 'tabs.sentimentAnalysis',
            selected: tabSelected === ENV.REACT_APP_SENTIMENT_REPORT
        }] : []),
        {
            route: ENV.REACT_APP_GENERAL_REPORT,
            title: t('tabs.generalReport'),
            key: 'tabs.generalReport',
            selected: tabSelected === ENV.REACT_APP_GENERAL_REPORT
        }
    ]

    const buildPlanModelValue = () => {
        const planLabel = usagePart1?.name || 'N/D'
        const modelLabel = usagePart1?.iaModel || 'N/A'

        if (usagePart1?.planModelLabel) {
            const parts = usagePart1.planModelLabel.split('+').map(part => part.trim())

            if (parts.length >= 2) {
                return (
                    <>
                        <span>{parts[0]}</span>
                        <br />
                        <span className='small-value'>{parts.slice(1).join(' + ')}</span>
                    </>
                )
            }
        }

        return (
            <>
                <span>{planLabel}</span>
                <br />
                <span className='small-value'>{modelLabel}</span>
            </>
        )
    }

    const formatCompactNumber = (value) => {
        const num = Number(value)
        if (!Number.isFinite(num)) return 'N/A'
        if (Math.abs(num) >= 1_000_000) return (num / 1_000_000).toFixed(2).replace(/\.?0+$/, '') + 'M'
        if (Math.abs(num) >= 1_000) return (num / 1_000).toFixed(2).replace(/\.?0+$/, '') + 'K'
        return num.toString()
    }

    const parseNumericValue = (value) => {
        if (typeof value === 'number') return value
        if (typeof value === 'string') {
            const normalized = value.replace(/,/g, '').trim()
            const parsed = Number(normalized)
            return Number.isFinite(parsed) ? parsed : null
        }
        return null
    }

    const backendTotalConversations = parseNumericValue(usagePart1?.totalConversationsUsedInAverage)
    const backendMonthlyAverage = parseNumericValue(usagePart1?.monthlyAverageElapsedDays)
    const averageTokens = parseNumericValue(usagePart1?.averageTokens)
        ?? parseNumericValue(usagePart1?.averageTokensPerConversation)
        ?? ((backendTotalConversations && backendTotalConversations > 0)
            ? (parseNumericValue(usagePart1?.tokensConsumed) ?? 0) / backendTotalConversations
            : null)

    const hasNoActivity = !backendTotalConversations || backendTotalConversations <= 0

    const effectiveMonthlyAverage = hasNoActivity ? 0 : backendMonthlyAverage
    const effectiveAverageTokens = Number.isFinite(averageTokens) ? averageTokens : 0

    // Resumen ejecutivo de sentimiento. El backend ya entrega los agregados calculados
    // en sentimentData.summary; el frontend solo los presenta (no recalcula nada).
    const summary = sentimentData?.summary

    const formatPercent = (value) => {
        const num = parseNumericValue(value)
        if (num == null) return 'N/D'
        return `${Math.round(num * 10) / 10}%`
    }

    // Mismos colores que las gráficas de sentimiento (SentimentDailyGraph):
    // verde = positivo, amarillo = neutral, rojo = negativo, gris = sin clasificar.
    const SENTIMENT_COLOR = { positive: '#69B75C', neutral: '#FACC15', negative: '#EF4444', unclassified: '#9CA3AF' }

    const renderPercent = (value, colorKey) => (
        <span style={{ color: SENTIMENT_COLOR[colorKey] }}>{formatPercent(value)}</span>
    )

    const TREND_ICON = { up: '↑', down: '↓', flat: '→' }
    const TREND_COLOR = { up: '#69B75C', down: '#EF4444', flat: '#9CA3AF' }

    const renderOverallSentiment = () => {
        const overall = summary?.overall
        return overall ? t(`executiveSummary.overall.${overall}`) : 'N/D'
    }

    const renderTrend = () => {
        const trend = summary?.trend
        if (!trend || !trend.direction) return t('executiveSummary.trend.na')
        const delta = parseNumericValue(trend.deltaPct)
        return (
            <span style={{ color: TREND_COLOR[trend.direction] || '#000000' }}>
                {TREND_ICON[trend.direction] || ''} {t(`executiveSummary.trend.${trend.direction}`)}
                {delta != null && (
                    <>
                        <br />
                        <span className='small-value'>{delta > 0 ? '+' : ''}{delta} pts</span>
                    </>
                )}
            </span>
        )
    }

    const periodSelector = (
        <Grid item xs={16} md={2}>
            <SimpleCard
                title={t('usagePeriod')}
            >
                <DropDownList
                    id='assistantTone'
                    name={'assistantTone'}
                    className="eticket-dropdown-field"
                    textField="text"
                    dataItemKey="value"
                    data={monthYearOptions}
                    value={monthYearOptions.find(option => option.value === date)}
                    onChange={handleChangeDate}
                />
            </SimpleCard>
        </Grid>
    )

    return (
        <>
            {isLoading
                ? <div className='loading'>
                    <SimpleLoading />
                </div>
                : (<div className={"dashboardView"}>
                    <Container>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <TabPure
                                        key={sentimentEnabled ? 'tabs-with-sentiment' : 'tabs-only-general'}
                                        tabs={tabs}
                                        tabStatus={setTabSelected}
                                        language={i18n.language}
                                        transl={t}
                                    />
                                    <Button
                                        svgIcon={arrowRotateCwIcon}
                                        rounded="full"
                                        fillMode="flat"
                                        onClick={onRefresh}
                                        title={t('refresh')}
                                        aria-label={t('refresh')}
                                        style={{
                                            borderRadius: '50%',
                                            color: '#0b7fa9',
                                            marginBottom: '2px'
                                        }}
                                    />
                                </div>
                            </Grid>

                            {/* PESTAÑA: Análisis de sentimiento */}
                            {tabSelected === ENV.REACT_APP_SENTIMENT_REPORT && (
                                <>
                                    {/* Periodo + resumen ejecutivo (cards con los agregados del backend).
                                        Van en el mismo contenedor grid para que se alineen (como en Reporte general). */}
                                    <Grid container item xs={16} spacing={1} className="reportsAndStatisticsView-dashboard">
                                        {periodSelector}
                                        {summary && (
                                            <>
                                                <Grid item xs={16} md={2}>
                                                    <StatCard
                                                        className={'textCard'}
                                                        title={t('executiveSummary.overallSentiment')}
                                                        value={renderOverallSentiment()}
                                                    />
                                                </Grid>
                                                <Grid item xs={16} md={3}>
                                                    <StatCard
                                                        className={'textCard'}
                                                        title={t('executiveSummary.totalAnalyzed')}
                                                        value={formatCompactNumber(summary.totalAnalyzed ?? 0)}
                                                    />
                                                </Grid>
                                                <Grid item xs={16} md={4}>
                                                    <StatCard
                                                        className={'textCard'}
                                                        title={t('executiveSummary.trendVsPrevious')}
                                                        value={renderTrend()}
                                                    />
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                    {summary && (
                                    <Grid container item xs={16} spacing={1} className="reportsPercentageSentimentalView">
                                        <Grid item xs={16} md={2} style={{ cursor: 'pointer' }} onClick={() => handleSentimentClick('positive')}>
                                            <StatCard
                                                className={'textCard sentiment-clickable'}
                                                title={t('executiveSummary.pctPositive')}
                                                value={renderPercent(summary.positive?.pct, 'positive')}
                                            />
                                        </Grid>
                                        <Grid item xs={16} md={2} style={{ cursor: 'pointer' }} onClick={() => handleSentimentClick('neutral')}>
                                            <StatCard
                                                className={'textCard sentiment-clickable'}
                                                title={t('executiveSummary.pctNeutral')}
                                                value={renderPercent(summary.neutral?.pct, 'neutral')}
                                            />
                                        </Grid>
                                        <Grid item xs={16} md={2} style={{ cursor: 'pointer' }} onClick={() => handleSentimentClick('negative')}>
                                            <StatCard
                                                className={'textCard sentiment-clickable'}
                                                title={t('executiveSummary.pctNegative')}
                                                value={renderPercent(summary.negative?.pct, 'negative')}
                                            />
                                        </Grid>
                                    </Grid>
                                    )}

                                    <Grid item xs={12} md={8}>
                                        <SentimentDailyGraph data={sentimentData} height={360} />
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        <AttentionStatusGraph data={sentimentData} height={360} />
                                    </Grid>
                                </>
                            )}

                            {/* PESTAÑA: Reporte general */}
                            {tabSelected === ENV.REACT_APP_GENERAL_REPORT && (
                                <>
                                    <Grid container item xs={16} spacing={1} className="reportsAndStatisticsView-dashboard">
                                        {periodSelector}

                                        <Grid item xs={16} md={2}>
                                            <StatCard
                                                className={'textCard'}
                                                title={t("name")}
                                                value={buildPlanModelValue()}
                                            />
                                        </Grid>

                                        <Grid item xs={16} md={4}>
                                            <StatCard
                                                className={'textCard'}
                                                title={t("tokensVsPlan")}
                                                value={
                                                    <>
                                                        <span>{formatCompactNumber(usagePart1?.tokensConsumed ?? 0)}</span>
                                                        <span> / {usagePart1?.tokensPlan != null ? formatCompactNumber(usagePart1.tokensPlan) : 'N/A'}</span>
                                                        <br />
                                                        <span style={{ fontSize: '12px', color: '#000000' }}>{t('averageTokensPerConversationLabel', { averageTokens: formatCompactNumber(averageTokens) })}</span>
                                                    </>
                                                }
                                            />
                                        </Grid>

                                        <Grid item xs={16} md={3}>
                                            <StatCard
                                                className={'textCard'}
                                                title={t("averagePerDay")}
                                                value={hasNoActivity ? 0 : usagePart1.dailyAverageConversations}
                                            />
                                        </Grid>
                                    </Grid>

                                    {/* Segunda fila: TokenVolumeGraph */}
                                    <Grid item xs={16} md={9} className="rasd-graph">
                                        <SimpleCard>
                                            <p class='dashboardView-card-title'>{t('tokenGraphTitle')}</p>
                                            <TokenVolumeGraph
                                                data={dailyTokenUsage}
                                                monthlyAverage={effectiveMonthlyAverage}
                                                t={t}
                                            />
                                        </SimpleCard>
                                    </Grid>

                                    <Grid item xs={16} md={3} className="rasd-speedometer">
                                        <SimpleCard style={{ height: '100%' }}>
                                            <ExpenseSpeedometer data={usagePart1} t={t} />
                                        </SimpleCard>
                                    </Grid>

                                    <Grid item xs={12} md={12} className="rasd-graph" style={{ paddingRight: 0, width: '100%' }}>
                                        <SimpleCard style={{ width: '100%' }}>
                                            <p class='dashboardView-card-title'>{t('tokensGraphTitle')}</p>
                                            <TokenVolumeGraph
                                                data={usagePart1?.dailyTokenUsage}
                                                monthlyAverage={effectiveAverageTokens}
                                                t={t}
                                                totalLabel={t('totalTokens')}
                                                yAxisLabel={t('tokensYAxis')}
                                                dailySeriesLabel={t('tokens')}
                                                averageSeriesLabel={t('averageTokensLine')}
                                                showBarValueLabels={false}
                                            />
                                        </SimpleCard>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Container>
                </div>)
            }
        </>
    )
}

export { UsageView as default }